import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import BoxListItem from './BoxListItem';
import ProgressFooter from './ProgressFooter';
import { ORDER_STEPS } from '../../Order';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useCatalog from '@/hooks/useCatalog';
import useLocalCart, { LineItem } from '@/hooks/useLocalCart';
import { Product } from '@/context/types';
import { SuccessNotification } from '@/components/toast/toastMessages';
import { motion } from 'framer-motion';
import ProductDetails from '@/pages/Product/ProductDetails';
import { useModalActions } from '@/components/Modals/useModalActions';
import { MODAL_KEY } from '@/constants';
import { BaseButton } from '@/components/Buttons/base';
import { CircleChevronLeft } from 'lucide-react';

export const BoxBuilderSkeleton = () => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{
        duration: 0.4,
        ease: [0.22, 1, 0.36, 1], // Matches your Apple-style easing curve
      }}
      className="grid grid-cols-1 lg:grid-cols-2 min-h-screen items-start bg-brand-frost"
    >
      {/* 1. Left Column (Header & Hero) */}
      <div className="flex flex-col gap-6 lg:gap-10 w-full h-full text-center pt-8 lg:pt-12 pb-6 lg:pb-10 px-4 md:px-10 items-center">
        {/* Header Text */}
        <div className="w-full max-w-lg mx-auto flex flex-col items-center gap-4">
          <div className="h-10 w-3/4 bg-brand-truffle/10 rounded-md animate-pulse" />
          <div className="h-4 w-1/2 bg-brand-truffle/10 rounded-md animate-pulse" />
        </div>

        {/* Product Details Hero */}
        <div className="mt-8 flex flex-col items-center w-full">
          {/* Image Square */}
          <div className="w-full max-w-sm aspect-square bg-white rounded-[var(--radius-artisan)] shadow-lift mb-8 animate-pulse overflow-hidden">
            <div className="w-full h-full bg-brand-truffle/5" />
          </div>

          {/* Details Content */}
          <div className="text-left w-full max-w-md px-4 space-y-5">
            {/* Category */}
            <div className="h-2 w-1/4 bg-brand-truffle/10 rounded animate-pulse" />
            {/* Title */}
            <div className="h-10 w-5/6 bg-brand-truffle/10 rounded animate-pulse" />
            {/* Metadata (Calories/Allergens) */}
            <div className="flex items-center gap-3">
              <div className="h-3 w-20 bg-brand-truffle/10 rounded animate-pulse" />
              <div className="h-3 w-12 bg-brand-truffle/10 rounded animate-pulse" />
              <div className="h-3 w-16 bg-brand-truffle/10 rounded animate-pulse" />
            </div>
            {/* Description Paragraph */}
            <div className="space-y-2 pt-2">
              <div className="h-3 w-full bg-brand-truffle/10 rounded animate-pulse" />
              <div className="h-3 w-5/6 bg-brand-truffle/10 rounded animate-pulse" />
            </div>
            {/* Tags */}
            <div className="flex gap-2 pt-2">
              <div className="h-6 w-16 bg-brand-truffle/10 rounded-full animate-pulse" />
              <div className="h-6 w-20 bg-brand-truffle/10 rounded-full animate-pulse" />
            </div>
          </div>
        </div>
      </div>

      {/* 2. Right Column (Scrollable List Area) */}
      <div className="relative flex flex-col h-full justify-start lg:justify-center max-w-3xl mx-auto px-4 pb-24 lg:pb-[160px] pt-6 lg:pt-0 w-full">
        {/* Title: "Select X Items" */}
        <div className="h-8 w-48 bg-brand-truffle/10 rounded-md mb-4 animate-pulse" />

        {/* List Container */}
        <div className="relative flex flex-col w-full bg-white rounded-[var(--radius-artisan)] lift overflow-hidden">
          {/* Mock Scrollable Window */}
          <div className="max-h-[50vh] lg:max-h-[60vh] overflow-y-hidden">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div
                key={i}
                className="flex flex-row items-center justify-between w-full py-2 px-4 odd:bg-white even:bg-brand-frost/70"
              >
                {/* Image & Text Wrapper */}
                <div className="flex items-center gap-4 flex-1 pr-4">
                  {/* Avatar (Exactly 32x32) */}
                  <div className="w-[32px] h-[32px] shrink-0 rounded-full bg-brand-truffle/10 animate-pulse" />
                  {/* Title & Metadata */}
                  <div className="flex flex-col gap-2 w-full">
                    <div className="h-4 w-1/2 bg-brand-truffle/10 rounded animate-pulse" />
                    <div className="h-2 w-1/3 bg-brand-truffle/10 rounded animate-pulse" />
                  </div>
                </div>
                {/* Pill Button (Exactly 120x25) */}
                <div className="w-[120px] h-[25px] shrink-0 rounded-full bg-brand-truffle/10 animate-pulse" />
              </div>
            ))}
          </div>

          {/* Sticky Progress Footer Skeleton */}
          <div className="sticky bottom-0 left-0 w-full bg-white border-t border-brand-truffle/10 p-4 md:px-8 shadow-lift">
            <div className="flex flex-col gap-4">
              {/* Progress Bar Area */}
              <div className="flex flex-col gap-2">
                <div className="flex justify-between">
                  <div className="h-3 w-16 bg-brand-truffle/10 rounded animate-pulse" />
                  <div className="h-3 w-24 bg-brand-truffle/10 rounded animate-pulse" />
                </div>
                <div className="h-1.5 w-full bg-brand-truffle/5 rounded-full animate-pulse" />
              </div>

              {/* Total & Button Area */}
              <div className="flex justify-between items-end pt-1">
                <div className="flex flex-col gap-2">
                  <div className="h-2 w-12 bg-brand-truffle/10 rounded animate-pulse" />
                  <div className="h-6 w-20 bg-brand-truffle/10 rounded animate-pulse" />
                </div>
                <div className="h-10 w-32 rounded-full bg-brand-truffle/10 animate-pulse" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default function BoxBuilder() {
  const { addLineItem, removeLineItem, cart, updateBoxContents } =
    useLocalCart();
  const { openModal } = useModalActions(MODAL_KEY.CONFIRM_USE_DEFAULT_ADDRESS);
  const { hierarchy } = useCatalog();
  const navigate = useNavigate();

  // 1. Core State
  const [activeDetails, setActiveDetails] = useState<Product>();
  const [selectedItems, setSelectedItems] = useState<
    Record<string, Partial<LineItem>>
  >({});
  const [searchParams, setSearchParams] = useSearchParams();

  const editId = searchParams.get('id');
  const urlBoxType = searchParams.get('box');
  const urlMixType = searchParams.get('mix')?.toLowerCase().trim();

  // 2. Synchronous Box Resolution (No useEffect required to find the box!)
  const existingBox = useMemo(() => {
    if (!editId) return undefined;
    return cart.lines.find((item) => item.localId === editId);
  }, [editId, cart.lines]);

  const handleToDiscardChanges = (e: any) => {
    e?.preventDefault();
    navigate(`/order?step=menu`, { replace: true });
  };

  // Derive the effective types. If editing, grab them from the existing item.
  const boxType = urlBoxType || existingBox?.id || '';
  const mixType = urlMixType || existingBox?.mixType || '';
  const selectedBox = existingBox ?? hierarchy?.boxes?.[mixType]?.[boxType];

  // 3. Safe Hydration Effect
  useEffect(() => {
    if (!hierarchy || !hierarchy.mixAndMatchBoxes?.[mixType]) return;

    // A. Set the featured product display to the first available mix item
    const availableMixes = hierarchy.mixAndMatchBoxes[mixType];
    if (availableMixes.length > 0) {
      setActiveDetails(availableMixes[0]);
    }

    // B. Pre-fill the box if we are in "Edit" mode
    if (existingBox?.contents) {
      const prefilled = existingBox.contents.reduce(
        (dict: Record<string, Partial<LineItem>>, item) => {
          dict[item.id!] = item;
          return dict;
        },
        {},
      );
      setSelectedItems(prefilled);
    } else {
      // Clear items if creating a brand new box
      setSelectedItems({});
    }
  }, [hierarchy, mixType, existingBox]); // Safely re-runs if the URL params or cart item changes

  // Derived State
  const totalSelected = useMemo(() => {
    return Object.values(selectedItems).reduce(
      (sum, s) => sum + (s.quantity || 0),
      0,
    );
  }, [selectedItems]);

  const maxCapacity = selectedBox?.capacity || 0;
  const isFull = totalSelected >= maxCapacity;

  // Handlers
  const handleAdd = (productId: string, index: number) => {
    if (!isFull) {
      setSelectedItems((prev) => ({
        ...prev,
        [productId]: {
          ...(prev?.[productId] ??
            hierarchy?.mixAndMatchBoxes?.[mixType]?.[index]),
          quantity: (prev[productId]?.quantity || 0) + 1,
        },
      }));
    }
  };

  const handleRemove = (productId: string) => {
    setSelectedItems((prev) => {
      const current = prev[productId]?.quantity || 0;
      if (current <= 1) {
        const newState = { ...prev };
        delete newState[productId];
        return newState;
      }
      return {
        ...prev,
        [productId]: { ...prev[productId], quantity: current - 1 },
      };
    });
  };

  const handleAddToCart = () => {
    if (!selectedBox || totalSelected !== maxCapacity) return;

    // Construct the finalized payload
    const completeBoxPayload = {
      ...selectedBox,
      quantity: existingBox?.quantity || 1, // Preserve their quantity if editing
      mixType,
      contents: Object.values(selectedItems as Record<string, LineItem>),
      localId: existingBox?.localId || window.crypto.randomUUID(), // Preserve original ID for updates
    };

    if (existingBox) {
      // UPDATE FLOW
      removeLineItem(existingBox.localId!); // Drop old
      addLineItem(completeBoxPayload); // Insert new

      SuccessNotification({
        title: `Box Updated`,
        message: `Your changes to ${selectedBox.title} have been saved.`,
      });
    } else {
      // CREATE FLOW
      addLineItem(completeBoxPayload);

      SuccessNotification({
        title: `Added ${selectedBox.title}`,
        message: `Your hand-picked assortment is ready for checkout.`,
      });
    }

    // Navigate back to the menu
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('box');
    newParams.delete('mix');
    newParams.delete('id'); // Ensure we clear the edit ID
    newParams.set('step', ORDER_STEPS[1]);

    setSearchParams(newParams, { replace: true });
  };

  const stepValue = searchParams.get('step');
  if (stepValue !== 'customize_box') {
    return null;
  }

  if (!hierarchy) return <BoxBuilderSkeleton />;

  // ------------------------------------------------------------------
  // FATAL VALIDATION: Trigger the Error Boundary (404)
  // ------------------------------------------------------------------
  const isMissingEditTarget = Boolean(editId && !existingBox);
  const isInvalidBoxType = !hierarchy.boxes?.[mixType]?.[boxType];
  const isInvalidMixType = !hierarchy.mixAndMatchBoxes?.[mixType];

  if (isMissingEditTarget || isInvalidBoxType || isInvalidMixType) {
    // Throwing directly in the render body instantly bypasses the rest of the
    // component and bubbles up to your nearest <ErrorBoundary> wrapper.
    throw new Error(
      '404_NOT_FOUND: The requested box or mix configuration does not exist.',
    );
  }

  // The Flattened Slot Record

  return (
    <div
      id="customize-box-bundle"
      className="relative grid grid-cols-1 lg:grid-cols-2 min-h-screen items-start bg-brand-frost"
    >
      <BaseButton
        onClick={handleToDiscardChanges}
        variant="none"
        className={`font-[500] text-[12px] leading-[1.8em] w-fit !px-0 !py-0 !rounded-full flex items-center justify-center gap-2 duration-300 whitespace-nowrap !fixed top-[95px] left-[15px] hover:scale-105 z-10`}
      >
        <CircleChevronLeft size={'2.5rem'} color="#3d2012" />
      </BaseButton>
      {/* Header Section */}
      <div className="flex flex-col gap-6 lg:gap-10 w-full h-full text-center pt-8 lg:pt-12 pb-6 lg:pb-10 px-4 md:px-10 lg:sticky lg:top-0">
        <div>
          <h1 className="font-display text-4xl md:text-5xl text-text-heading mb-4">
            Customize your box
          </h1>
          <p className="font-body text-text-muted max-w-lg mx-auto">
            Mix and match your favorite flavors to create the perfect
            assortment.
          </p>
        </div>
        {/* Only mount the component if details exist to keep the Fiber tree stable */}
        {activeDetails ? (
          <ProductDetails product={activeDetails} />
        ) : (
          <div className="flex-1" />
        )}
      </div>
      {/* Scrollable List Area */}
      <div className="relative flex flex-col h-full justify-start lg:justify-center max-w-3xl mx-auto px-4 pb-24 lg:pb-[160px] pt-4 lg:pt-6 w-full">
        <h4 className="font-display !tracking-medium !font-[900] text-xl lg:!text-[2em] !text-text-heading mb-3 lg:mb-4">
          {`Select ${selectedBox?.capacity} Items`}
        </h4>
        {/* List Container with rounded corners and shadow */}
        <div className="relative flex flex-col w-full bg-white rounded-[var(--radius-artisan)] lift overflow-hidden">
          {/* Scrollable Window */}
          <div className="max-h-[50vh] lg:max-h-[60vh] overflow-y-auto">
            {hierarchy.mixAndMatchBoxes[mixType]?.map((product, index) => (
              <BoxListItem
                key={product.id}
                product={product}
                selectedQty={selectedItems[product.id]?.quantity || 0}
                onAdd={(e) => handleAdd(product.id, index)}
                onRemove={(e) => handleRemove(product.id)}
                onDetailClick={() => setActiveDetails(product)}
                disabled={isFull}
              />
            ))}
          </div>
          {/* 4. Sticky Footer */}
          <ProgressFooter
            currentCount={totalSelected}
            maxCount={maxCapacity}
            onAddToCart={handleAddToCart}
            isExisting={!!existingBox}
            basePrice={15.0} // Wire this up to your selectedBox base price if applicable
          />
        </div>
      </div>
    </div>
  );
}
